'use client';

import { Button } from '@/components/shared/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shared/ui/select';
import { RefreshCw, Filter, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type DateRange = 'today' | '7d' | '30d' | '90d';
export type ChannelFilter = 'all' | 'walk_in' | 'booking';
export type StatusFilter = 'all' | 'completed' | 'in_progress' | 'pending' | 'cancelled';

export interface FilterState {
  dateRange: DateRange;
  staffId: string | null;
  serviceId: string | null;
  channel: ChannelFilter;
  status: StatusFilter;
}

export interface DashboardFilterBarProps {
  staff: { id: string; name: string }[];
  services: { id: string; name: string }[];
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onRefresh: () => void;
  isLoading?: boolean;
}

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
];

const CHANNEL_OPTIONS: { value: ChannelFilter; label: string }[] = [
  { value: 'all', label: 'All Channels' },
  { value: 'walk_in', label: 'Walk-ins' },
  { value: 'booking', label: 'Bookings' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function DashboardFilterBar({
  staff,
  services,
  filters,
  onFilterChange,
  onRefresh,
  isLoading,
}: DashboardFilterBarProps) {
  const hasActiveFilters =
    filters.staffId ||
    filters.serviceId ||
    filters.channel !== 'all' ||
    filters.status !== 'all';

  const clearFilters = () => {
    onFilterChange({
      ...filters,
      staffId: null,
      serviceId: null,
      channel: 'all',
      status: 'all',
    });
  };

  return (
    <div
      className="sticky top-0 z-40 -mx-3 sm:-mx-4 lg:-mx-6 px-3 sm:px-4 lg:px-6 py-2 bg-background/95 backdrop-blur-sm border-b border-border"
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* Date Range - Always visible */}
        <div className="flex p-0.5 bg-muted rounded-lg">
          {DATE_RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => onFilterChange({ ...filters, dateRange: option.value })}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                filters.dateRange === option.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Barber Filter */}
        <Select
          value={filters.staffId || 'all'}
          onValueChange={(value) =>
            onFilterChange({ ...filters, staffId: value === 'all' ? null : value })
          }
        >
          <SelectTrigger className="h-8 w-[130px] text-xs">
            <SelectValue placeholder="All Barbers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Barbers</SelectItem>
            {staff.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Service Filter */}
        <Select
          value={filters.serviceId || 'all'}
          onValueChange={(value) =>
            onFilterChange({ ...filters, serviceId: value === 'all' ? null : value })
          }
        >
          <SelectTrigger className="h-8 w-[130px] text-xs">
            <SelectValue placeholder="All Services" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Services</SelectItem>
            {services.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Channel Filter */}
        <Select
          value={filters.channel}
          onValueChange={(value) =>
            onFilterChange({ ...filters, channel: value as ChannelFilter })
          }
        >
          <SelectTrigger className="h-8 w-[120px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHANNEL_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select
          value={filters.status}
          onValueChange={(value) =>
            onFilterChange({ ...filters, status: value as StatusFilter })
          }
        >
          <SelectTrigger className="h-8 w-[120px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground gap-1"
          >
            <X className="h-3 w-3" />
            Clear
          </Button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Active Filters Indicator */}
        {hasActiveFilters && (
          <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
            <Filter className="h-3 w-3" />
            <span>Filters active</span>
          </div>
        )}

        {/* Refresh Button */}
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={onRefresh}
          disabled={isLoading}
        >
          <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
        </Button>
      </div>
    </div>
  );
}
